import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { GET_DASHBOARD_INSIGHTS } from '../../apollo/queries/dashboard_insights'
import { LIST_MANUFACTURERS_BASIC } from '../../apollo/queries/listManufacturers'
import { BeatLoader } from 'react-spinners'
import FormInput from '../../components/atoms/Input/Form-Input'
import { formatName, formatString, generateDateLabels } from '../../utils'
import CustomDatePicker from '../../components/atoms/Input/CustomDatePicker'
import moment from 'moment'
import { useUserContext } from '../../context/UserContext'

export default function DashboardStats() {
  const { data: listManufacturersData } = useQuery(LIST_MANUFACTURERS_BASIC);
  const brandOptions =
    listManufacturersData?.listManufacturers?.map(item => ({
      label: formatName(item?.brand),
      value: item?.brand,
    })) || []
  const { userData } = useUserContext()
  const isSimpleAdmin =
    userData?.brand !== 'saddlefitadmin' && (userData?.type === 'Admin' || userData?.type === 'Member')
  const [brandName, setBrandName] = useState(
    isSimpleAdmin ? userData?.brand?.toLowerCase() : '',
  )

  useEffect(() => {
    if (!isSimpleAdmin && brandOptions?.length > 0 && !brandName) {
      setBrandName(brandOptions[0]?.value)
    }
  }, [brandOptions, isSimpleAdmin, brandName])

  const [dateRange, setDateRange] = useState({
    start: moment().subtract(7, 'days').format('MM-DD-YYYY'),
    end: moment().format('MM-DD-YYYY'),
  })

  const handleStartDateChange = date => {
    setDateRange(prev => ({ ...prev, start: date }))
  }

  const handleEndDateChange = date => {
    setDateRange(prev => ({ ...prev, end: date }))
  }

  const startDateIsValid = currentDate => {
    if (dateRange.end) {
      const end = moment(dateRange.end, 'MM-DD-YYYY')
      return currentDate.isSameOrBefore(end, 'day')
    }
    return true
  }

  const endDateIsValid = currentDate => {
    if (dateRange.start) {
      const start = moment(dateRange.start, 'MM-DD-YYYY')
      return currentDate.isSameOrAfter(start, 'day')
    }
    return true
  }

  const { data, loading } = useQuery(GET_DASHBOARD_INSIGHTS, {
    variables: {
      brandName: brandName,
      startDate: dateRange.start
        ? moment(dateRange.start, 'MM-DD-YYYY').format('YYYY-MM-DD')
        : '',
      endDate: dateRange.end
        ? moment(dateRange.end, 'MM-DD-YYYY').format('YYYY-MM-DD')
        : '',
    },
    fetchPolicy: 'network-only',
    skip:
      !brandName ||
      (!!dateRange.start && !dateRange.end) ||
      (!dateRange.start && !!dateRange.end),
  })

  const dashboardInsights = data?.getDashboardInsights

  const orderByProductData = dashboardInsights?.totalSalesByProduct || []
  const orderByStatesData = dashboardInsights?.totalSalesByState || []

  const dateLabels =
    dateRange.start && dateRange.end
      ? generateDateLabels(dateRange.start, dateRange.end)
      : []

  const totalSalesData = dateLabels.map((label, i) => ({
    name: label,
    sales: dashboardInsights?.totalSales?.[i]?.shopifyTotal || 0,
  }))

  return (
    <div>
      <div className="w-full items-center flex-wrap flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="w-full md:w-auto">
          <h1 className="admin-dashboard">Dashboard</h1>
        </div>
        <div className="flex flex-col sm:flex-row  items-end gap-6 w-full md:w-auto">
          {!isSimpleAdmin && (
            <FormInput
              name="brandName"
              type="select2"
              label="Brand Name"
              height={54}
              placeholder="Select Brand"
              options={brandOptions}
              selectedValues={brandOptions.find(
                option => option.value === brandName,
              )}
              validations={{
                required: { value: true, message: 'Brand Name is required' },
              }}
              className="w-full md:!w-[195px]"
              onChange={option => setBrandName(option?.value)}
            />
          )}

          <CustomDatePicker
            clearable
            allowCurrentAndPreviousYear
            value={dateRange.start}
            onChange={handleStartDateChange}
            label="Start Date"
            placeholder="Select Date"
            isValidDate={startDateIsValid}
            inputClassName="font-montserrat text-[14px] w-[95%] sm:w-[85%] md:w-auto px-3 sm:px-5 h-[50px] mt-2 rounded-[50px] border border-[#999]"
            className="w-full md:!w-[195px]"
          />

          <CustomDatePicker
            clearable
            allowCurrentAndPreviousYear
            value={dateRange.end}
            onChange={handleEndDateChange}
            label="End Date"
            placeholder="Select Date"
            isValidDate={endDateIsValid}
            inputClassName="font-montserrat text-[14px] w-[95%] sm:w-[85%] md:w-auto px-3 sm:px-5 h-[50px] mt-2 rounded-[50px] border border-[#999]"
            className="w-full md:!w-[195px]"
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 mb-6">
        <div className="bg-white rounded-xl shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 font-montserrat">
              Gross Sales
            </h3>
          </div>
          {loading ? (
            <BeatLoader color={'#2b364b'} className="mt-5" />
          ) : (
            <p className="text-2xl font-bold mt-2 font-montserrat">
              {`$${Number(
                dashboardInsights?.grossSales ?? 0,
              ).toLocaleString()}`}
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 font-montserrat">
              Cart Abandonment
            </h3>
          </div>
          {loading ? (
            <BeatLoader color={'#2b364b'} className="mt-5" />
          ) : (
            <p className="text-2xl font-bold mt-2 font-montserrat">
              {`${Number(
                dashboardInsights?.cartAbandoment ?? 0,
              ).toLocaleString()}`}
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 font-montserrat">
              Total Orders
            </h3>
          </div>
          {loading ? (
            <BeatLoader color={'#2b364b'} className="mt-5" />
          ) : (
            <p className="text-2xl font-bold mt-2 font-montserrat">
              {`${Number(dashboardInsights?.orders ?? 0).toLocaleString()}`}
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 font-montserrat">
              Total Orders Fulfilled
            </h3>
          </div>
          {loading ? (
            <BeatLoader color={'#2b364b'} className="mt-5" />
          ) : (
            <p className="text-2xl font-bold mt-2 font-montserrat">
              {`${Number(
                dashboardInsights?.ordersFulFilled ?? 0,
              ).toLocaleString()}`}
            </p>
          )}
        </div>
      </div>

      {/* // usage for Total Sales */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6  mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
          <h3 className="text-lg font-semibold mb-2 sm:mb-0 font-montserrat">
            Total Sales
          </h3>
        </div>

        <div className="h-[300px] sm:h-[400px]">
          {renderChartOrMessage(
            totalSalesData,
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                className="font-montserrat"
                data={totalSalesData.map(item => ({
                  ...item,
                  name: formatString(item.name),
                }))}
                margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  interval={0}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  tickFormatter={value => `$${Number(value).toLocaleString()}`}
                  padding={{ bottom: 10 }}
                />
                <Tooltip
                  formatter={value => `$${Number(value).toLocaleString()}`}
                />
                <Legend formatter={() => 'Total Sales'} />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#2b364b"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>,
            loading,
          )}
        </div>
      </div>

      {/* Total Sales by Location */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6 min-h-[400px] max-h-[500px] overflow-auto mb-6">
        <h3 className="text-lg font-semibold mb-4 font-montserrat">
          Total Sales by Location
        </h3>
        {renderChartOrMessage(
          orderByStatesData,
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              layout="vertical"
              data={orderByStatesData
                .filter(item => item.totalPrice > 0)
                .map(item => ({
                  ...item,
                  name: String(item.name)?.toUpperCase(),
                }))}
              className="font-montserrat"
              margin={{ top: 10, right: 50, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[0, 'dataMax + 1000']}
                tickFormatter={value => `$${Number(value).toLocaleString()}`}
              />
              <YAxis type="category" dataKey="name" />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'Total Price') {
                    return `$${Number(value).toLocaleString()}`
                  }
                  return Number(value).toLocaleString()
                }}
              />
              <Legend />
              <Bar
                dataKey="totalPrice"
                name="Total Price"
                fill="#2b364b"
                barSize={30}
              />
              <Bar
                dataKey="totalCount"
                name="Total Count"
                fill="#5c80b6"
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>,
          loading,
        )}
      </div>

      {/* Total Sales by Product */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6 min-h-[400px] max-h-[500px] overflow-auto">
        <h3 className="text-lg font-semibold mb-4 font-montserrat">
          Total Sales by Product
        </h3>
        {renderChartOrMessage(
          orderByProductData,
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={orderByProductData
                .filter(item => item.totalPrice > 0)
                .map(item => ({
                  ...item,
                  name: formatString(item.name),
                }))}
              layout="vertical"
              className="font-montserrat"
              margin={{ top: 10, right: 50, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[0, 'dataMax + 1000']}
                tickFormatter={value => `$${Number(value).toLocaleString()}`}
              />
              <YAxis type="category" dataKey="name" />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'Total Price') {
                    return `$${Number(value).toLocaleString()}`
                  }
                  return Number(value).toLocaleString()
                }}
              />
              <Legend />
              <Bar
                dataKey="totalPrice"
                name="Total Price"
                fill="#2b364b"
                barSize={30}
              />
              <Bar
                dataKey="totalCount"
                name="Total Count"
                fill="#5c80b6"
                barSize={30}
              />
            </BarChart>
          </ResponsiveContainer>,
          loading,
        )}
      </div>
    </div>
  )
}

const renderChartOrMessage = (data, chart, loading = false) => {
  if (loading) {
    return (
      <div className="h-[300px] sm:h-[400px] flex justify-center items-center">
        <BeatLoader color={'#2b364b'} />
      </div>
    )
  }

  if (!data || data.length === 0 || data.every(item => item.totalPrice === 0)) {
    return (
      <div className="h-[300px] sm:h-[400px] flex flex-col justify-center items-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-gray-400 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M9 17v-4a2 2 0 012-2h2a2 2 0 012 2v4m-7 0h8m-4-4v4m-5 4h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-gray-500 text-base font-montserrat">
          No data available
        </p>
        <p className="text-sm text-gray-400 mt-1 font-montserrat">
          Try adjusting your date range or filters
        </p>
      </div>
    )
  }

  return chart
}
